import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import { sha256 } from "js-sha256";

import { Input, Select, Label, Button, Checkbox } from "../../common";
import { FormErrorMessage } from "../../messages";
import {
  ACCOUNT_TYPE_STRING,
  BANKS,
  CHAINS,
  CHAINS_MAP,
} from "../../../constants";
import { selectCurrentUser } from "../../../store/reducers/user";
import { useAddWalletMutation } from "../../../store/services/wallets";
import { BaseWallet, Chain, ChainType } from "../../../types/WalletTypes";
import { getBasePath } from "../../../utils/router";
import { track } from "../../../utils/tracking";

type Props = {
  callback: (data?: any) => void;
};

type Inputs = {
  walletCorrect: boolean;
  walletSupportsCoin: boolean;
  personResponsible: boolean;
} & BaseWallet;

export const NewBankAccountForm: FC<Props> = ({ callback }) => {
  const {
    account_type,
    allowed_outgoing_chains,
    ga_id,
    contact_name,
    first_name,
    last_name,
  } = useSelector(selectCurrentUser);

  const [checkedDisclaimersCount, setCheckedDisclaimersCount] = useState(0);
  const location = useLocation();

  const allAllowedChains: Chain[] = useMemo(
    () =>
      Object.values(allowed_outgoing_chains || [])
        .flat()
        .filter((chain) => chain !== Chain.Euro),
    [allowed_outgoing_chains]
  );

  const basePath = useMemo(
    () => getBasePath(location.pathname),
    [location.pathname]
  );

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid },
    watch,
    setValue,
  } = useForm<Inputs>({
    defaultValues: {
      name: "",
      chain: undefined,
      bank_name: "",
      account_number: "",
      beneficiary_name: contact_name || `${first_name} ${last_name}`,
      personResponsible: false,
    },
  });

  const allowedChains = useMemo(
    () =>
      CHAINS.filter(
        ({ value, type }) =>
          type === ChainType.FIAT && allAllowedChains.includes(value)
      ),
    [allAllowedChains]
  );

  const chainOptions = useMemo(
    () => allowedChains.map(({ value, name }) => ({ value, label: name })),
    [allowedChains]
  );

  // Set initial chain value if there's only one allowed chain
  useEffect(() => {
    if (chainOptions.length === 1) {
      setValue("chain", chainOptions[0].value, { shouldValidate: true });
    }
  }, [chainOptions, setValue]);

  const selectedChain = watch("chain");
  const selectedBankId = watch("bank_name");
  const selectedChainDetails = CHAINS_MAP[selectedChain];
  const bankCodeLabel = selectedChain === Chain.INR ? "IFSC code" : "Bank code";

  const bankDetails = useMemo(
    () => BANKS.find(({ id }) => id === selectedBankId),
    [selectedBankId]
  );

  const bankCodeOptions = useMemo(
    () => (bankDetails && bankDetails.codes ? bankDetails.codes : []),
    [bankDetails]
  );

  const trackWallet = useCallback(
    (event: string, props?: Record<string, string | undefined>) => {
      track(event, {
        account_type: ACCOUNT_TYPE_STRING[account_type],
        user_id: ga_id,
        uid: ga_id,
        caller: basePath,
        ...props,
      });
    },
    [account_type, basePath, ga_id]
  );

  const trackWalletField = useCallback(
    (name: string, value?: string) => {
      if (value) {
        trackWallet(`wallet_${name}_added`, {
          [name]: (() => {
            switch (name) {
              case "chain":
                return value;
              case "address":
                return sha256(value);
              default:
                return undefined;
            }
          })(),
        });
      }
    },
    [trackWallet]
  );

  const trackDisclaimers = useCallback((checked: boolean) => {
    setCheckedDisclaimersCount((curr) => (checked ? curr + 1 : curr - 1));
  }, []);

  useEffect(() => trackWallet("wallet_creation_initiated"), [trackWallet]);

  useEffect(() => {
    if (checkedDisclaimersCount === 1) {
      trackWallet("wallet_disclaimers_checked");
    }
  }, [checkedDisclaimersCount, trackWallet]);

  const [addWallet, { data, isSuccess, isError, error }] =
    useAddWalletMutation();

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    async ({
      name,
      chain,
      bank_name,
      bank_code,
      account_number,
      beneficiary_name,
    }) => {
      try {
        await addWallet({
          name,
          chain,
          bank_name,
          bank_code: bank_name === "INDICI" ? "-" : bank_code,
          account_number,
          beneficiary_name,
        });

        trackWallet("wallet_created", {
          currency: chain,
          bank_name,
          account_number,
          beneficiary_name,
        });
      } catch (error) {
        throw error;
      }
    },
    [addWallet, trackWallet]
  );

  useEffect(() => {
    if (isSuccess && callback) {
      callback(data);
    }
  }, [isSuccess, callback, data]);

  return (
    <form
      className="flex flex-col gap-6 md:gap-8"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Label label="Currency">
        <Controller
          name="chain"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              {...field}
              placeholder="Select currency"
              options={chainOptions}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                trackWalletField(field.name, e.target.value || "");
                field.onBlur();
              }}
            />
          )}
        />
      </Label>

      {selectedChainDetails?.features?.includes("bank_code") && (
        <Label label={bankCodeLabel}>
          <Controller
            name="bank_code"
            control={control}
            rules={{ required: bankCodeOptions.length > 0 }}
            render={({ field }) => (
              <Input
                type="text"
                {...field}
                onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                  trackWalletField(field.name, e.target.value || "");
                  field.onBlur();
                }}
              />
            )}
          />
        </Label>
      )}

      <Label label="Account number">
        <Controller
          name="account_number"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              type="text"
              {...field}
              note={
                <strong>
                  Saving or NRO accounts only! Payments to NRE accounts will
                  fail.
                </strong>
              }
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                trackWalletField(field.name, e.target.value);
                field.onBlur();
              }}
            />
          )}
        />
      </Label>

      <Label label="Account nickname">
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              type="text"
              {...field}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                trackWalletField(field.name, e.target.value);
                field.onBlur();
              }}
            />
          )}
        />
      </Label>

      <Label label="Beneficiary name">
        <Controller
          name="beneficiary_name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              type="text"
              {...field}
              note={
                <>
                  Wrong name and can't change it?&nbsp;
                  <button className="text-purple-700 contact-support-btn">
                    Contact support
                  </button>
                </>
              }
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                trackWalletField(field.name, e.target.value);
                field.onBlur();
              }}
            />
          )}
        />
      </Label>

      <div className="flex flex-col gap-y-4">
        <Controller
          name="personResponsible"
          control={control}
          rules={{ required: true }}
          render={({ field, field: { value } }) => (
            <Checkbox
              {...field}
              checked={value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                field.onChange(e);
                trackDisclaimers(e.target.checked);
              }}
            >
              <span className="text-gray-300">
                I'm fully responsible for accurate details. If they're wrong, I
                could lose my funds permanently.
              </span>
            </Checkbox>
          )}
        />
      </div>

      <FormErrorMessage className="mb-6" isError={isError} error={error} />

      <div className="flex flex-col items-center pb-0 h-18 !z-[50] bg-white">
        <Button
          block
          type="submit"
          variant={Button.variant.PRIMARY}
          disabled={!isDirty || !isValid}
        >
          Save
        </Button>
      </div>
    </form>
  );
};
